import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RoleSelect } from "@/pages/Onboarding";

function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/discover";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const Shell = ({ title, eyebrow, children }) => (
  <main className="grid min-h-[calc(100vh-4rem)] lg:grid-cols-2">
    <div className="relative hidden overflow-hidden lg:block">
      <img src="/images/f2.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#09090b]" />
    </div>
    <div className="flex items-center px-6 py-14 sm:px-16">
      <div className="rise w-full max-w-md">
        <p className="eyebrow mb-3">{eyebrow}</p>
        <h1 className="font-display mb-10 text-5xl">{title}</h1>
        {children}
      </div>
    </div>
  </main>
);

const GoogleBtn = () => (
  <button type="button" data-testid="google-login-btn" onClick={googleLogin}
    className="mt-4 w-full rounded-full border border-white/20 py-3 text-sm transition-colors hover:border-white hover:bg-white hover:text-black">
    Continue with Google
  </button>
);

const Field = ({ label, id, ...p }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="eyebrow">{label}</Label>
    <Input id={id} data-testid={`${id}-input`} required className="h-12 bg-zinc-900" {...p} />
  </div>
);

export function Login() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/discover" replace />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", f);
      setUser(data);
      nav("/discover");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Welcome back." eyebrow="Sign in">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <Field label="Email" id="login-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field label="Password" id="login-password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <button disabled={busy} data-testid="login-submit-btn" className="btn-red w-full rounded-full py-3 font-semibold disabled:opacity-50">Sign in</button>
      </form>
      <GoogleBtn />
      <p className="mt-8 text-sm text-zinc-500">New here? <Link to="/register" data-testid="go-register-link" className="text-red-500 hover:text-red-400">Create an account</Link></p>
    </Shell>
  );
}

export function Register() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", dob: "", role_type: "sugar_baby", country: "" });
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/discover" replace />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!agree) return toast.error("Please confirm you are 18 or older");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", f);
      setUser(data);
      toast.success("Welcome to Diamond Luxury");
      nav("/verify");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Join the circle." eyebrow="Create account · 18+">
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <RoleSelect value={f.role_type} onChange={(v) => setF({ ...f, role_type: v })} />
        <Field label="Display name" id="register-name" value={f.name} onChange={set("name")} />
        <Field label="Email" id="register-email" type="email" value={f.email} onChange={set("email")} />
        <Field label="Password (8+ characters)" id="register-password" type="password" minLength={8} value={f.password} onChange={set("password")} />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Date of birth" id="register-dob" type="date" value={f.dob} onChange={set("dob")} />
          <Field label="Country" id="register-country" value={f.country} onChange={set("country")} />
        </div>
        <label className="flex items-center gap-3 text-sm text-zinc-400">
          <input type="checkbox" data-testid="register-age-confirm" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="accent-red-600" />
          I confirm I am 18 or older and agree to ID verification.
        </label>
        <button disabled={busy} data-testid="register-submit-btn" className="btn-red w-full rounded-full py-3 font-semibold disabled:opacity-50">Create account</button>
      </form>
      <GoogleBtn />
      <p className="mt-8 text-sm text-zinc-500">Already a member? <Link to="/login" data-testid="go-login-link" className="text-red-500 hover:text-red-400">Sign in</Link></p>
    </Shell>
  );
}
